import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { BackButton } from '../components/BackButton';
import { ClassMark, NpcMark } from '../components/Pictogram';
import { paths } from '../navigation/paths';
import { errorMessage } from '../api/client';
import {
  addCombatParticipants,
  endCombat,
  executeCombatAction,
  fetchCombat,
  fleeCombatParticipant,
  startCombat,
} from '../api/combat';
import type {
  CombatActionTarget,
  CombatActionType,
  CombatLastAction,
  CombatParticipant,
  CombatPreview,
  CombatState,
  EffectTriggerResult,
  NpcAbility,
  Spell,
} from '../types';
import { armorPool, bonusSources, isConsumablePotion, isCurrencyItem, stackInventoryItems } from '../utils/itemStats';
import { burnTriggerAmount, isBurnEffect, spellNeededTargets } from '../utils/spellEffect';
import { revokeNpcEffect, setGameNpcHealth, triggerNpcEffect } from '../api/npcs';
import { revokeCharacterEffect, setCharacterHealth, triggerCharacterEffect } from '../api/games';
import { VitalSliderDialog } from '../components/VitalSliderDialog';
import { Pictogram } from '../components/Pictogram';
import { CombatLogDrawer } from '../components/CombatLogDrawer';
import { useGamepadNavigation } from '../input/useGamepadNavigation';

type KitEntry = { name: string; preview?: CombatPreview; effect?: string; formula?: string; result?: number | null };

function gamepadTargetSide(entry: KitEntry): 'allies' | 'opposite' {
  const kind = entry.preview?.kind;
  if (kind === 'heal' || kind === 'mana') return 'allies';
  if (kind === 'damage') return 'opposite';
  const blob = `${entry.name} ${entry.effect || ''} ${entry.formula || ''}`;
  if (/heil|heal|mana/i.test(blob) && !/schaden|damage/i.test(blob)) return 'allies';
  return 'opposite';
}

function effectCaption(entry: KitEntry) {
  if (entry.preview?.summary) return entry.preview.summary;
  if (entry.preview && typeof entry.preview.amount === 'number') {
    const kindLabel = entry.preview.kind === 'heal' ? 'Heilung' : entry.preview.kind === 'mana' ? 'Mana' : entry.preview.kind === 'effect' ? 'Effekt' : 'Schaden';
    return entry.preview.kind === 'effect' ? 'Effekt' : `${kindLabel} ${entry.preview.amount}`;
  }
  if (typeof entry.result === 'number') {
    return `Schaden ${entry.result}`;
  }
  const raw = String(entry.effect || entry.formula || '');
  const match = raw.match(/-?\d+/);
  if (!match) return '';
  const kindLabel = /mana|mp/i.test(raw)
    ? 'Mana'
    : /heil|heal/i.test(raw)
      ? 'Heilung'
      : /rüstung|ruestung|armor/i.test(raw)
        ? 'Rüstung'
        : 'Schaden';
  return `${kindLabel} ${Math.abs(parseInt(match[0], 10))}`;
}

function participantArmor(part: CombatParticipant) {
  const pool = armorPool(bonusSources({ items: part.items, effects: part.effects }));
  const current = typeof part.armor === 'number' ? Math.max(0, part.armor) : pool.current;
  const max = pool.max > 0 ? Math.max(pool.max, current) : current;
  return { current, max };
}

function armorCaption(current: number, max: number) {
  return max > 0 ? `Rüstung ${current}/${max}` : `Rüstung ${current}`;
}

function scaleCaption(part: CombatParticipant) {
  const factor = part.scaling_factor || 1;
  const shown = factor.toLocaleString('de-DE', { maximumFractionDigits: 2 });
  const mark = Math.abs(factor - 1) < 0.001 ? '' : ` · ×${shown}`;
  if (part.scaling_mode === 'combat_heroes' || part.scaling_mode === 'combat_foes') {
    const heroes = part.hero_count || 0;
    return `${heroes === 1 ? '1 Held' : `${heroes} Helden`}${mark}`;
  }
  if (part.scaling_mode === 'player_count') {
    const players = part.player_count || 0;
    return `${players === 1 ? '1 Spieler' : `${players} Spieler`}${mark}`;
  }
  if (part.scaling_mode === 'level') {
    return `Level ${part.party_level || 1}${mark}`;
  }
  if (part.scaling_mode === 'per_stat') {
    const factors = part.stat_factors || {};
    const bits = [
      ['Leben', factors.health],
      ['Schaden', factors.damage],
      ['Bewegung', factors.movement],
    ].flatMap(([label, value]) => {
      const factorValue = Number(value || 1);
      if (Math.abs(factorValue - 1) < 0.001) return [];
      return [`${label} ×${factorValue.toLocaleString('de-DE', { maximumFractionDigits: 2 })}`];
    });
    const usesHeroes = ['health', 'damage', 'movement'].some(stat => {
      const rules = part.stat_scaling?.[stat as 'health' | 'damage' | 'movement'] || {};
      return rules.combat_heroes != null || rules.combat_foes != null;
    });
    const heroPrefix = usesHeroes
      ? `${(part.hero_count || 0) === 1 ? '1 Held' : `${part.hero_count || 0} Helden`} · `
      : '';
    return `${heroPrefix}${bits.join(' · ')}`;
  }
  return '';
}

function barPercent(current: number | null | undefined, max: number | null | undefined) {
  const ceiling = max || 0;
  if (ceiling <= 0) return 0;
  return Math.max(0, Math.min(100, ((current || 0) / ceiling) * 100));
}

const NARROW_COMBAT_QUERY = '(max-width: 1023px)';

function sourceId(part: CombatParticipant) {
  return part.kind === 'npc' ? part.game_npc_id : part.character_id;
}

function isNarrowCombatViewport() {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  return window.matchMedia(NARROW_COMBAT_QUERY).matches;
}

function armorNotice(targets?: CombatActionTarget[]) {
  const list = targets || [];
  const absorbed = list.filter((target) => Number(target.absorbed_armor) > 0);
  const brokenOnly = list.filter(
    (target) => Number(target.absorbed_armor || 0) <= 0 && (target.broken?.length || 0) > 0,
  );
  const rows = [...absorbed, ...brokenOnly];
  if (rows.length === 0) return '';
  const showNames = list.length > 1;
  return rows.map((target) => {
    const prefix = showNames && target.name ? `${target.name}: ` : '';
    const broken = (target.broken || [])
      .map((part) => `${part.name || 'Rüstung'} kaputt`)
      .join(', ');
    const absorbedArmor = Number(target.absorbed_armor) || 0;
    if (absorbedArmor <= 0) return `${prefix}${broken}`;
    const remain = typeof target.armor === 'number' ? ` (noch ${target.armor}` : '';
    if (remain && broken) return `${prefix}Rüstung −${absorbedArmor}${remain}, ${broken})`;
    if (remain) return `${prefix}Rüstung −${absorbedArmor}${remain})`;
    if (broken) return `${prefix}Rüstung −${absorbedArmor} · ${broken}`;
    return `${prefix}Rüstung −${absorbedArmor}`;
  }).join(' · ');
}

function appendArmorNotice(message: string, targets?: CombatActionTarget[]) {
  const extra = armorNotice(targets);
  if (!extra) return message;
  const base = message.replace(/[.\s]+$/, '');
  return `${base} · ${extra}.`;
}

function formatActionToast(result: CombatLastAction) {
  const verb = result.kind === 'heal' ? 'heilt' : result.kind === 'effect' ? 'wirkt' : 'trifft';
  const detail = result.summary || `für ${result.amount}`;
  const message = `${result.actor} ${verb} ${detail}.`;
  if (result.kind !== 'damage') return message;
  return appendArmorNotice(message, result.targets);
}

function formatTriggerToast(result: EffectTriggerResult) {
  return appendArmorNotice(result.message || 'Brennen ausgelöst', result.targets);
}

function combatSide(part: CombatParticipant) {
  if (part.side === 'heroes' || part.side === 'foes') return part.side;
  return part.kind === 'npc' ? 'foes' : 'heroes';
}

function PlayerMark({ name }: { name?: string | null }) {
  const label = (name || '').trim();
  if (!label) return null;
  return <span className="combat-player-name">{label}</span>;
}

function CombatAvatar({ part }: { part: CombatParticipant }) {
  if (part.kind === 'npc') {
    return <NpcMark icon={part.icon} iconUrl={part.icon_url} npcType={part.class_or_type} />;
  }
  return <ClassMark klass={part.class_or_type} icon={part.icon} />;
}

export function CombatPage() {
  const { gameId: gameIdParam } = useParams();
  const gameId = gameIdParam ? parseInt(gameIdParam, 10) : NaN;
  const navigate = useNavigate();
  const { handleLogout, setToastMessage } = useApp();
  const [state, setState] = useState<CombatState | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [pickedChars, setPickedChars] = useState<number[]>([]);
  const [pickedFoeChars, setPickedFoeChars] = useState<number[]>([]);
  const [pickedNpcs, setPickedNpcs] = useState<number[]>([]);
  const [actorId, setActorId] = useState<number | null>(null);
  const [action, setAction] = useState<CombatActionType | null>(null);
  const [spellId, setSpellId] = useState<number | null>(null);
  const [itemId, setItemId] = useState<number | null>(null);
  const [abilityName, setAbilityName] = useState<string | null>(null);
  const [lastAction, setLastAction] = useState<CombatLastAction | null>(null);
  const [hpEdit, setHpEdit] = useState<CombatParticipant | null>(null);
  const [targetIds, setTargetIds] = useState<number[]>([]);
  const [logOpen, setLogOpen] = useState(false);
  const pageRef = useGamepadNavigation({
    regions: ['setup', 'heroes', 'foes', 'actions', 'extras'],
    profile: 'combat',
    hint: 'LB Helden · RB Gegner · A weiter · B zurück · Y Übersicht · View halten beendet',
  });
  const [extraFoesOpen, setExtraFoesOpen] = useState(() => !isNarrowCombatViewport());
  const extraFoesTouched = useRef(false);
  const syncEpoch = useRef(0);
  const busyRef = useRef(false);
  const hpEditRef = useRef<CombatParticipant | null>(null);
  const actorIdRef = useRef<number | null>(null);
  busyRef.current = busy;
  hpEditRef.current = hpEdit;
  actorIdRef.current = actorId;

  const refreshCombat = useCallback(async (background = false) => {
    if (!Number.isFinite(gameId)) return;
    const ticket = ++syncEpoch.current;
    try {
      const next = await fetchCombat(gameId);
      if (ticket !== syncEpoch.current) return;
      setState(next);
      setLoadError(null);
      const participants = next.combat?.participants || [];
      const currentActor = actorIdRef.current;
      if (currentActor != null && !participants.some((part) => part.id === currentActor)) {
        setActorId(null);
        setAction(null);
        setSpellId(null);
        setItemId(null);
        setAbilityName(null);
        setTargetIds([]);
      }
    } catch (err) {
      if (!background && ticket === syncEpoch.current) {
        setLoadError(errorMessage(err, 'Kampf konnte nicht geladen werden.'));
      }
    }
  }, [gameId]);

  useEffect(() => {
    if (!Number.isFinite(gameId)) {
      navigate(paths.masterGames);
    }
  }, [gameId, navigate]);

  useEffect(() => {
    void refreshCombat(false);
  }, [refreshCombat]);

  useEffect(() => {
    if (!Number.isFinite(gameId)) return undefined;
    const timer = window.setInterval(() => {
      if (busyRef.current || hpEditRef.current) return;
      void refreshCombat(true);
    }, 3000);
    return () => window.clearInterval(timer);
  }, [gameId, refreshCombat]);

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return undefined;
    const media = window.matchMedia(NARROW_COMBAT_QUERY);
    const onChange = () => {
      if (extraFoesTouched.current) return;
      setExtraFoesOpen(!media.matches);
    };
    if (typeof media.addEventListener === 'function') {
      media.addEventListener('change', onChange);
      return () => media.removeEventListener('change', onChange);
    }
    return undefined;
  }, []);

  const combat = state?.combat || null;
  const participants = combat?.participants || [];
  const actor = participants.find(part => part.id === actorId) || null;
  const inCombatChar = new Set(participants.filter(p => p.kind === 'character').map(sourceId));
  const inCombatNpc = new Set(participants.filter(p => p.kind === 'npc').map(sourceId));
  const availableChars = (state?.roster.characters || []).filter(part => !inCombatChar.has(sourceId(part)));
  const availableNpcs = (state?.roster.npcs || []).filter(part => !inCombatNpc.has(sourceId(part)));

  const actionPicked = useMemo(() => {
    if (!actorId || !action) return false;
    if (action === 'spell' && !spellId) return false;
    if (action === 'item' && !itemId) return false;
    if (action === 'ability' && !abilityName) return false;
    return true;
  }, [actorId, action, spellId, itemId, abilityName]);

  const toast = (message: string) => {
    setToastMessage(message);
    window.setTimeout(() => setToastMessage(null), 4000);
  };

  const applyState = (next: CombatState) => {
    syncEpoch.current += 1;
    setState(next);
    if (next.last_action) setLastAction(next.last_action);
  };

  const clearTurn = () => {
    setActorId(null);
    setAction(null);
    setSpellId(null);
    setItemId(null);
    setAbilityName(null);
    setTargetIds([]);
  };

  const togglePick = (list: number[], setter: (ids: number[]) => void, id: number) => {
    setter(list.includes(id) ? list.filter(item => item !== id) : [...list, id]);
  };

  const pickHero = (id: number) => {
    togglePick(pickedChars, setPickedChars, id);
    setPickedFoeChars(list => list.filter(item => item !== id));
  };

  const pickFoeChar = (id: number) => {
    togglePick(pickedFoeChars, setPickedFoeChars, id);
    setPickedChars(list => list.filter(item => item !== id));
  };

  const handleStart = async () => {
    setBusy(true);
    try {
      applyState(await startCombat(gameId, pickedChars, pickedNpcs, pickedFoeChars));
      setPickedChars([]);
      setPickedFoeChars([]);
      setPickedNpcs([]);
      toast('Kampf gestartet.');
    } catch (err) {
      setLoadError(errorMessage(err, 'Kampf konnte nicht gestartet werden.'));
    } finally {
      setBusy(false);
    }
  };

  const handleAdd = async () => {
    setBusy(true);
    try {
      applyState(await addCombatParticipants(gameId, pickedChars, pickedNpcs, pickedFoeChars));
      setPickedChars([]);
      setPickedFoeChars([]);
      setPickedNpcs([]);
    } catch (err) {
      setLoadError(errorMessage(err, 'Teilnehmer konnten nicht hinzugefügt werden.'));
    } finally {
      setBusy(false);
    }
  };

  const saveHealth = async (value: number) => {
    if (!hpEdit || !Number.isFinite(gameId)) return;
    setBusy(true);
    setLoadError(null);
    try {
      if (hpEdit.kind === 'npc' && hpEdit.game_npc_id) {
        await setGameNpcHealth(gameId, hpEdit.game_npc_id, value);
      } else if (hpEdit.character_id) {
        await setCharacterHealth(hpEdit.character_id, value);
      }
      setHpEdit(null);
      await refreshCombat(false);
    } catch (err) {
      setLoadError(errorMessage(err, 'Leben konnte nicht gesetzt werden.'));
    } finally {
      setBusy(false);
    }
  };

  const executeOn = async (ids: number[]) => {
    if (!actorId || !action || busy || ids.length === 0) return;
    setBusy(true);
    setLoadError(null);
    try {
      const next = await executeCombatAction(gameId, {
        actor_id: actorId,
        action,
        target_ids: ids,
        spell_id: spellId || undefined,
        item_id: itemId || undefined,
        ability_name: abilityName || undefined,
      });
      applyState(next);
      setAction(null);
      setSpellId(null);
      setItemId(null);
      setAbilityName(null);
      setTargetIds([]);
      if (next.last_action) {
        toast(formatActionToast(next.last_action));
      }
    } catch (err) {
      setLoadError(errorMessage(err, 'Aktion fehlgeschlagen.'));
    } finally {
      setBusy(false);
    }
  };

  const handleTriggerEffect = async (part: CombatParticipant, effectId: number) => {
    setBusy(true);
    setLoadError(null);
    try {
      if (part.kind === 'npc' && part.game_npc_id) {
        const result = await triggerNpcEffect(gameId, part.game_npc_id, effectId);
        toast(formatTriggerToast(result));
      } else if (part.character_id) {
        const result = await triggerCharacterEffect(part.character_id, effectId);
        toast(formatTriggerToast(result));
      }
      await refreshCombat(false);
    } catch (err) {
      setLoadError(errorMessage(err, 'Effekt konnte nicht ausgelöst werden.'));
    } finally {
      setBusy(false);
    }
  };

  const handleRevokeEffect = async (part: CombatParticipant, effectId: number) => {
    setBusy(true);
    setLoadError(null);
    try {
      if (part.kind === 'npc' && part.game_npc_id) {
        const result = await revokeNpcEffect(gameId, part.game_npc_id, effectId);
        toast(result.message || 'Effekt beendet');
      } else if (part.character_id) {
        const result = await revokeCharacterEffect(part.character_id, effectId);
        toast(result.message || 'Effekt beendet');
      }
      await refreshCombat(false);
    } catch (err) {
      setLoadError(errorMessage(err, 'Effekt konnte nicht beendet werden.'));
    } finally {
      setBusy(false);
    }
  };

  const handleFlee = async (part: CombatParticipant) => {
    if (busy) return;
    setBusy(true);
    try {
      const next = await fleeCombatParticipant(gameId, part.id);
      applyState(next);
      if (part.id === actorId) {
        clearTurn();
      } else {
        setTargetIds((ids) => ids.filter((id) => id !== part.id));
      }
      toast(`${part.name} flieht aus dem Kampf.`);
    } catch (err) {
      setLoadError(errorMessage(err, 'Flucht ist fehlgeschlagen.'));
    } finally {
      setBusy(false);
    }
  };

  const handleEnd = async () => {
    setBusy(true);
    try {
      applyState(await endCombat(gameId));
      clearTurn();
      setLastAction(null);
      toast('Kampf beendet. HP und Mana bleiben gespeichert.');
    } catch (err) {
      setLoadError(errorMessage(err, 'Kampf konnte nicht beendet werden.'));
    } finally {
      setBusy(false);
    }
  };

  const chooseAction = (next: CombatActionType, extra?: { spell?: number; item?: number; ability?: string }) => {
    const same = action === next
      && (extra?.spell ?? null) === spellId
      && (extra?.item ?? null) === itemId
      && (extra?.ability ?? null) === abilityName;
    if (same) {
      setAction(null);
      setSpellId(null);
      setItemId(null);
      setAbilityName(null);
      setTargetIds([]);
      return;
    }
    setAction(next);
    setSpellId(extra?.spell ?? null);
    setItemId(extra?.item ?? null);
    setAbilityName(extra?.ability ?? null);
    setTargetIds([]);
  };

  const selectedSpell = (actor?.spells || []).find(spell => spell.id === spellId);
  const wantedTargets = action === 'spell'
    ? spellNeededTargets(selectedSpell?.effect, selectedSpell?.preview?.needed_targets)
    : 1;

  const handleCardClick = (part: CombatParticipant) => {
    if (busy) return;
    if (actionPicked) {
      if (wantedTargets <= 1) {
        void executeOn([part.id]);
        return;
      }
      const next = targetIds.includes(part.id)
        ? targetIds.filter(id => id !== part.id)
        : [...targetIds, part.id];
      setTargetIds(next);
      if (next.length >= wantedTargets) {
        void executeOn(next);
      }
      return;
    }
    if (part.defeated) return;
    if (part.id === actorId) {
      clearTurn();
      return;
    }
    setActorId(part.id);
    setAction(null);
    setSpellId(null);
    setItemId(null);
    setAbilityName(null);
  };

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') clearTurn();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  if (!Number.isFinite(gameId)) {
    return null;
  }

  const heroes = participants.filter(part => combatSide(part) === 'heroes');
  const foes = participants.filter(part => combatSide(part) === 'foes');
  const burning = participants.flatMap((part) => (
    (part.effects || []).filter(isBurnEffect).map((effect) => ({ part, effect }))
  ));
  const foeTitle = foes.length > 0 && foes.every(part => part.kind === 'character')
    ? '⚔ Spieler'
    : '☠ Gegner';
  const actionLabel = action === 'attack'
    ? 'Angriff'
    : action === 'spell'
      ? (actor?.spells || []).find(spell => spell.id === spellId)?.name
      : action === 'item'
        ? (actor?.items || []).find(item => item.id === itemId)?.name
        : action === 'ability'
          ? abilityName
          : null;

  return (
    <div className="combat-page medieval" ref={pageRef} data-gamepad-root>
      <header className="game-header">
        <h1>⚔️ Kampf {state?.game.name ? `· ${state.game.name}` : ''}</h1>
        <div className="header-buttons">
          <BackButton fallback={paths.masterGame(gameId)} className="medieval-button" modeSwitch>
            ← Übersicht
          </BackButton>
          <button
            type="button"
            className={`medieval-button combat-log-toggle${logOpen ? ' is-open' : ''}`}
            data-gamepad-toggle="log"
            onClick={() => setLogOpen(open => !open)}
            aria-expanded={logOpen}
            aria-controls="combat-log-drawer"
          >
            Kampflog{(state?.log?.length || 0) > 0 ? ` (${state?.log?.length})` : ''}
          </button>
          {combat && (
            <button type="button" className="medieval-button" data-gamepad-end-combat onClick={() => void handleEnd()} disabled={busy}>
              Kampf beenden
            </button>
          )}
          <button type="button" className="medieval-button logout-button" onClick={handleLogout}>
            Ausloggen
          </button>
        </div>
      </header>

      {loadError && <p className="form-error" role="alert">{loadError}</p>}

      {!combat && (
        <section className="combat-setup overview-section" data-gamepad-region="setup">
          <h2>Teilnehmer wählen</h2>
          <p className="section-hint">
            Aktuelle Spieler auf die Helden- und/oder Gegnerseite. Für PvP mindestens einen Spieler je Seite; NPCs sind optional.
          </p>
          <div className="combat-setup-grid">
            <div>
              <h3>Helden</h3>
              {(state?.roster.characters || []).length === 0 ? (
                <p className="no-content">Keine Charaktere in diesem Spiel.</p>
              ) : (
                (state?.roster.characters || []).map(part => (
                  <label
                    key={`hero-${part.character_id}`}
                    className={`combat-pick character ${pickedChars.includes(part.character_id as number) ? 'is-picked' : ''}`}
                  >
                    <input
                      type="checkbox"
                      checked={pickedChars.includes(part.character_id as number)}
                      onChange={() => pickHero(part.character_id as number)}
                    />
                    <span className="combat-pick-avatar" aria-hidden><CombatAvatar part={part} /></span>
                    <span className="combat-pick-copy">
                      <span className="combat-pick-main">
                        {part.name} ({part.class_or_type}) · HP {part.health}/{part.max_health}
                      </span>
                      <PlayerMark name={part.player_name} />
                    </span>
                  </label>
                ))
              )}
            </div>
            <div>
              <h3>Spieler als Gegner</h3>
              {(state?.roster.characters || []).length === 0 ? (
                <p className="no-content">Keine Charaktere in diesem Spiel.</p>
              ) : (
                (state?.roster.characters || []).map(part => (
                  <label
                    key={`foe-${part.character_id}`}
                    className={`combat-pick foe-player ${pickedFoeChars.includes(part.character_id as number) ? 'is-picked' : ''}`}
                  >
                    <input
                      type="checkbox"
                      checked={pickedFoeChars.includes(part.character_id as number)}
                      onChange={() => pickFoeChar(part.character_id as number)}
                    />
                    <span className="combat-pick-avatar" aria-hidden><CombatAvatar part={part} /></span>
                    <span className="combat-pick-copy">
                      <span className="combat-pick-main">
                        {part.name} als Gegner · HP {part.health}/{part.max_health}
                      </span>
                      <PlayerMark name={part.player_name} />
                    </span>
                  </label>
                ))
              )}
            </div>
            <div>
              <h3>NPC-Gegner</h3>
              {(state?.roster.npcs || []).length === 0 ? (
                <p className="no-content">Keine Gegner zugeordnet. Lege sie in der Spielübersicht an.</p>
              ) : (
                <div className="npc-pick-grid">
                {(state?.roster.npcs || []).map(part => (
                  <label
                    key={part.game_npc_id}
                    className={`combat-pick npc ${pickedNpcs.includes(part.game_npc_id as number) ? 'is-picked' : ''}`}
                  >
                    <input
                      type="checkbox"
                      checked={pickedNpcs.includes(part.game_npc_id as number)}
                      onChange={() => togglePick(pickedNpcs, setPickedNpcs, part.game_npc_id as number)}
                    />
                    <span className="combat-pick-avatar" aria-hidden><CombatAvatar part={part} /></span>
                    <span className="combat-pick-copy">
                      {part.name} · HP {part.health}/{part.max_health}
                    </span>
                  </label>
                ))}
                </div>
              )}
            </div>
          </div>
          <button
            type="button"
            className="medieval-button create-btn"
            disabled={busy || pickedChars.length === 0}
            onClick={() => void handleStart()}
          >
            Kampf starten
          </button>
        </section>
      )}

      <CombatLogDrawer
        open={logOpen}
        entries={state?.log || []}
        fights={state?.fights || []}
        onClose={() => setLogOpen(false)}
      />

      {combat && (
        <>
          {lastAction && (
            <p className={`combat-log ${lastAction.kind === 'heal' ? 'is-heal' : 'is-hit'}`} role="status">
              <span className="combat-log-mark" aria-hidden>{lastAction.kind === 'heal' ? '✚' : '⚔'}</span>
              {lastAction.actor} wirkt {lastAction.action}: {lastAction.summary || `${lastAction.kind === 'heal' ? 'Heilung' : lastAction.kind === 'mana' ? 'Mana' : lastAction.kind === 'effect' ? 'Effekt' : 'Schaden'} ${lastAction.kind === 'effect' ? (lastAction.targets[0]?.effect || lastAction.action) : lastAction.amount}`}
              {lastAction.mana_cost ? ` · Mana −${lastAction.mana_cost}` : ''}
              {' → '}
              {lastAction.targets.map(t => `${t.name} ${t.health}/${t.max_health}`).join(', ')}
            </p>
          )}

          <div className={`combat-board ${actionPicked ? 'is-aiming' : ''}`} data-gamepad-aiming={actionPicked ? 'true' : 'false'}>
            <section className="combat-arena combat-arena-heroes" data-gamepad-region="heroes">
              <h2>🛡 Helden <span className="combat-arena-count">{heroes.length}</span></h2>
              <div className="combat-card-grid">
                {heroes.map(part => (
                  <ParticipantCard
                    key={part.id}
                    part={part}
                    isActor={part.id === actorId}
                    aiming={actionPicked}
                    targetIndex={targetIds.indexOf(part.id)}
                    onSelect={() => handleCardClick(part)}
                    onEditHp={() => setHpEdit(part)}
                    onTriggerEffect={(effectId) => void handleTriggerEffect(part, effectId)}
                    onRevokeEffect={(effectId) => void handleRevokeEffect(part, effectId)}
                  />
                ))}
                {heroes.length === 0 && <p className="no-content">Noch keine Helden.</p>}
              </div>
            </section>
            <div className="combat-vs" aria-hidden>
              <span>VS</span>
            </div>
            <section className="combat-arena combat-arena-foes" data-gamepad-region="foes" data-gamepad-parent="heroes">
              <h2>{foeTitle} <span className="combat-arena-count">{foes.length}</span></h2>
              <div className="combat-card-grid">
                {foes.map(part => (
                  <ParticipantCard
                    key={part.id}
                    part={part}
                    isActor={part.id === actorId}
                    aiming={actionPicked}
                    targetIndex={targetIds.indexOf(part.id)}
                    onSelect={() => handleCardClick(part)}
                    onEditHp={() => setHpEdit(part)}
                    onTriggerEffect={(effectId) => void handleTriggerEffect(part, effectId)}
                    onRevokeEffect={(effectId) => void handleRevokeEffect(part, effectId)}
                  />
                ))}
                {foes.length === 0 && <p className="no-content">Keine Gegner — Übung nur mit Helden.</p>}
              </div>
            </section>
          </div>

          {burning.length > 0 && (
            <section className="combat-actions combat-burn-bar" aria-label="Brennen auslösen">
              <div className="combat-actions-copy">
                <h2>Brennen</h2>
                <p className="section-hint">Schaden auslösen, so oft der Effekt gelten soll. ✕ beendet ihn.</p>
              </div>
              <div className="combat-action-list">
                {burning.map(({ part, effect }) => {
                  const amount = burnTriggerAmount(effect);
                  const burnKey = `${part.id}:${effect.id}`;
                  const burnName = effect.name || 'Brennen';
                  return (
                    <div key={`burn-${burnKey}`} className="combat-burn-pair">
                      <button
                        type="button"
                        className="medieval-button combat-act combat-act-burn"
                        data-gamepad-combat-burn
                        data-gamepad-burn="trigger"
                        data-gamepad-burn-key={burnKey}
                        disabled={busy}
                        onClick={() => void handleTriggerEffect(part, effect.id)}
                      >
                        🔥 {burnName}
                        {amount > 0 && <span className="combat-act-amount">Schaden {amount}</span>}
                        <span className="combat-act-mana">{part.name}</span>
                      </button>
                      <button
                        type="button"
                        className="remove-item-btn combat-burn-end"
                        data-gamepad-combat-burn
                        data-gamepad-burn="end"
                        data-gamepad-burn-key={burnKey}
                        disabled={busy}
                        title="Effekt beenden"
                        aria-label={`${burnName} bei ${part.name} beenden`}
                        onClick={() => void handleRevokeEffect(part, effect.id)}
                      >
                        ✕
                      </button>
                    </div>
                  );
                })}
              </div>
            </section>
          )}

          <section className="combat-actions" data-gamepad-region="actions" data-gamepad-parent="heroes">
            <div className="combat-actions-copy">
              <h2>Zug</h2>
              {actor && (
                <div className="combat-actor-chip">
                  <span className="combat-actor-chip-avatar" aria-hidden><CombatAvatar part={actor} /></span>
                  <strong>{actor.name}</strong>
                  <PlayerMark name={actor.player_name} />
                </div>
              )}
              <p className="section-hint">
                {!actor && 'Kachel anklicken, um den Actor zu setzen.'}
                {actor && !actionPicked && (
                  <>Actor: {actor.name} <PlayerMark name={actor.player_name} /> — Aktion wählen, dann Zielkachel anklicken.</>
                )}
                {actor && actionPicked && wantedTargets <= 1 && (
                  <>Actor: {actor.name} <PlayerMark name={actor.player_name} /> wirkt {actionLabel} — Zielkachel anklicken.</>
                )}
                {actor && actionPicked && wantedTargets > 1 && (
                  <>
                    Actor: {actor.name} <PlayerMark name={actor.player_name} /> wirkt {actionLabel}
                    {' '}— {targetIds.length}/{wantedTargets} Ziele in Reihenfolge anklicken
                    {selectedSpell?.preview?.summary ? ` (${selectedSpell.preview.summary})` : ''}.
                  </>
                )}
              </p>
            </div>
            {actor && (
              <div className="combat-action-list" data-gamepad-combat-actions>
                <button type="button" className={`medieval-button combat-act combat-act-attack ${action === 'attack' ? 'is-picked' : ''}`} data-gamepad-combat-action data-gamepad-action-kind="attack" data-gamepad-target="opposite" onClick={() => chooseAction('attack')}>
                  <span aria-hidden>⚔ </span>Angriff
                  {typeof actor.damage === 'number' && (
                    <span className="combat-act-amount">Schaden {actor.damage}</span>
                  )}
                </button>
                {(actor.spells || []).map((spell: Spell) => {
                  const caption = effectCaption(spell);
                  return (
                    <button
                      key={spell.id}
                      type="button"
                      className={`medieval-button combat-act combat-act-spell ${action === 'spell' && spellId === spell.id ? 'is-picked' : ''}`}
                      data-gamepad-combat-action
                      data-gamepad-action-kind="spell"
                      data-gamepad-target={gamepadTargetSide(spell)}
                      onClick={() => chooseAction('spell', { spell: spell.id })}
                      title={spell.effect}
                    >
                      <Pictogram iconUrl={spell.icon_url} name={spell.name} kind="spell" className="combat-act-icon" />
                      {spell.name}
                      {caption && <span className="combat-act-amount">{caption}</span>}
                      <span className="combat-act-mana">{spell.mana_cost} Mana</span>
                    </button>
                  );
                })}
                {stackInventoryItems(actor.items || [])
                  .filter(({ item }) => isConsumablePotion(item) && !isCurrencyItem(item))
                  .map(({ item, count }) => {
                  const caption = effectCaption(item);
                  return (
                    <button
                      key={item.character_item_id || item.id}
                      type="button"
                      className={`medieval-button combat-act combat-act-item ${action === 'item' && itemId === item.id ? 'is-picked' : ''}`}
                      data-gamepad-combat-action
                      data-gamepad-action-kind="item"
                      data-gamepad-target={gamepadTargetSide(item)}
                      onClick={() => chooseAction('item', { item: item.id })}
                      title={item.effect}
                    >
                      <Pictogram iconUrl={item.icon_url} type={item.type} name={item.name} kind="item" className="combat-act-icon" />
                      <span className="combat-act-name">
                        {item.name}
                        {count > 1 && <span className="stack-badge" aria-label={`${count} Stück`}>×{count}</span>}
                      </span>
                      {caption && <span className="combat-act-amount">{caption}</span>}
                    </button>
                  );
                })}
                {(actor.abilities || []).map((ability: NpcAbility) => {
                  const caption = effectCaption(ability);
                  return (
                    <button
                      key={ability.name}
                      type="button"
                      className={`medieval-button combat-act combat-act-ability ${action === 'ability' && abilityName === ability.name ? 'is-picked' : ''}`}
                      data-gamepad-combat-action
                      data-gamepad-action-kind="ability"
                      data-gamepad-target={gamepadTargetSide(ability)}
                      onClick={() => chooseAction('ability', { ability: ability.name })}
                      title={ability.formula}
                    >
                      {ability.name}
                      {caption && <span className="combat-act-amount">{caption}</span>}
                    </button>
                  );
                })}
                <button
                  type="button"
                  className="medieval-button combat-act combat-flee"
                  data-gamepad-combat-action
                  data-gamepad-action-kind="flee"
                  disabled={busy}
                  onClick={() => void handleFlee(actor)}
                >
                  <img className="combat-act-icon" src="/icons/aktion-flucht.png" alt="" aria-hidden />
                  Fliehen
                </button>
                {actionPicked && wantedTargets > 1 && (
                  <button
                    type="button"
                    className="medieval-button create-btn"
                    data-gamepad-combat-action
                    data-gamepad-action-kind="confirm"
                    disabled={busy || targetIds.length === 0}
                    onClick={() => void executeOn(targetIds)}
                  >
                    Wirken ({targetIds.length}/{wantedTargets})
                  </button>
                )}
                <button type="button" className="medieval-button combat-cancel" data-gamepad-combat-action data-gamepad-action-kind="cancel" onClick={clearTurn}>
                  Abbrechen
                </button>
              </div>
            )}
          </section>

          {(availableChars.length > 0 || availableNpcs.length > 0) && (
            <section
              className={`overview-section combat-setup combat-extra${extraFoesOpen ? '' : ' is-collapsed'}`}
              data-gamepad-region="extras"
              data-gamepad-parent="foes"
            >
              <div className="combat-extra-head">
                <h2>Weitere Teilnehmer</h2>
                <button
                  type="button"
                  className="medieval-button combat-extra-toggle"
                  aria-expanded={extraFoesOpen}
                  aria-controls="combat-extra-foes"
                  onClick={() => {
                    extraFoesTouched.current = true;
                    setExtraFoesOpen((open) => !open);
                  }}
                >
                  {extraFoesOpen ? 'Gegner einklappen' : 'Gegner aufklappen'}
                </button>
              </div>
              <div className="combat-setup-grid">
                {availableChars.length > 0 && (
                  <div>
                    <h3>Helden</h3>
                    {availableChars.map(part => (
                      <label key={`add-hero-${part.character_id}`} className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={pickedChars.includes(part.character_id as number)}
                          onChange={() => pickHero(part.character_id as number)}
                        />
                        <span>{part.name}</span>
                        <PlayerMark name={part.player_name} />
                      </label>
                    ))}
                  </div>
                )}
                <div
                  id="combat-extra-foes"
                  className="combat-extra-foes"
                  aria-label="Zusätzliche Gegner"
                  hidden={!extraFoesOpen}
                >
                  {availableChars.length > 0 && (
                    <div>
                      <h3>Spieler als Gegner</h3>
                      {availableChars.map(part => (
                        <label key={`add-foe-${part.character_id}`} className="checkbox-label">
                          <input
                            type="checkbox"
                            checked={pickedFoeChars.includes(part.character_id as number)}
                            onChange={() => pickFoeChar(part.character_id as number)}
                          />
                          <span>{part.name} als Gegner</span>
                          <PlayerMark name={part.player_name} />
                        </label>
                      ))}
                    </div>
                  )}
                  {availableNpcs.length > 0 && (
                    <div>
                      <h3>NPC-Gegner</h3>
                      <div className="npc-pick-grid">
                        {availableNpcs.map(part => (
                          <label key={part.game_npc_id} className="checkbox-label">
                            <input
                              type="checkbox"
                              checked={pickedNpcs.includes(part.game_npc_id as number)}
                              onChange={() => togglePick(pickedNpcs, setPickedNpcs, part.game_npc_id as number)}
                            />
                            {part.name}
                          </label>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <button
                type="button"
                className="medieval-button"
                disabled={busy || (pickedChars.length === 0 && pickedFoeChars.length === 0 && pickedNpcs.length === 0)}
                onClick={() => void handleAdd()}
              >
                Hinzufügen
              </button>
            </section>
          )}
        </>
      )}
      {hpEdit && (
        <VitalSliderDialog
          title="❤️ HP ändern"
          name={hpEdit.name}
          initial={hpEdit.health || 0}
          max={hpEdit.max_health || 0}
          onClose={() => setHpEdit(null)}
          onApply={(value) => { void saveHealth(value); }}
        />
      )}
    </div>
  );
}

function ParticipantCard({
  part,
  isActor,
  aiming,
  targetIndex,
  onSelect,
  onEditHp,
  onTriggerEffect,
  onRevokeEffect,
}: {
  part: CombatParticipant;
  isActor: boolean;
  aiming: boolean;
  targetIndex: number;
  onSelect: () => void;
  onEditHp: () => void;
  onTriggerEffect: (effectId: number) => void;
  onRevokeEffect: (effectId: number) => void;
}) {
  const hp = barPercent(part.health, part.max_health);
  const mana = barPercent(part.mana, part.max_mana);
  const armor = participantArmor(part);
  const armorText = armorCaption(armor.current, armor.max);
  const showArmor = part.kind === 'character' || armor.current > 0 || armor.max > 0;
  const critical = hp > 0 && hp <= 30;
  const label = aiming
    ? `${part.name} als Ziel`
    : `${part.name}${isActor ? ', Actor' : ''}`;
  return (
    <article
      role="button"
      tabIndex={part.defeated && !aiming ? -1 : 0}
      aria-pressed={isActor}
      aria-label={label}
      data-gamepad-combat-card
      data-gamepad-side={combatSide(part)}
      className={`combat-card ${part.kind} ${combatSide(part) === 'foes' && part.kind === 'character' ? 'is-pvp-foe' : ''} ${isActor ? 'is-actor' : ''} ${aiming ? 'is-aiming' : ''} ${targetIndex >= 0 ? 'is-target' : ''} ${part.defeated ? 'is-down' : ''} ${critical ? 'is-critical' : ''}`}
      onClick={onSelect}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onSelect();
        }
      }}
    >
      {part.defeated && <span className="combat-down-stamp">Besiegt</span>}
      <div className="combat-card-avatar" aria-hidden><CombatAvatar part={part} /></div>
      <div className="combat-card-body">
        <header>
          <strong>{part.name}</strong>
          <PlayerMark name={part.player_name} />
          {isActor && <span className="combat-ribbon">Actor</span>}
          {targetIndex >= 0 && <span className="combat-ribbon combat-ribbon-target">Ziel {targetIndex + 1}</span>}
          {part.kind === 'character' && combatSide(part) === 'foes' && (
            <span className="combat-ribbon combat-ribbon-foe">Spieler</span>
          )}
          <span className="combat-card-class">
            {part.class_or_type}{part.level ? ` · Lvl ${part.level}` : ''}
          </span>
        </header>
        {part.kind === 'npc' && scaleCaption(part) && (
          <p className="combat-hero-scale">{scaleCaption(part)}</p>
        )}
        <button
          type="button"
          className={`combat-bar combat-hp is-settable ${critical ? 'is-critical' : ''}`}
          title={`HP ${part.health}/${part.max_health}`}
          aria-label={`HP von ${part.name} setzen`}
          onClick={(event) => {
            event.stopPropagation();
            onEditHp();
          }}
        >
          <div className="combat-bar-fill combat-hp-fill" style={{ width: `${hp}%` }} />
          <span>HP {part.health}/{part.max_health}</span>
        </button>
        {part.kind === 'character' && (
          <div className="combat-bar combat-mana" title={`Mana ${part.mana ?? 0}/${part.max_mana ?? 0}`}>
            <div className="combat-bar-fill combat-mana-fill" style={{ width: `${mana}%` }} />
            <span>Mana {part.mana ?? 0}/{part.max_mana ?? 0}</span>
          </div>
        )}
        {showArmor && (
          <div className="combat-bar combat-armor" title={armorText}>
            <div className="combat-bar-fill combat-armor-fill" style={{ width: `${barPercent(armor.current, armor.max)}%` }} />
            <span>{armorText}</span>
          </div>
        )}
        <div className="combat-stats">
          <span className="combat-stat">⚔ {part.damage}</span>
          <span className="combat-stat">➤ {part.movement}</span>
          {part.kind === 'character' && (
            <>
              <span className="combat-stat">💪 {part.strength ?? 10}</span>
              <span className="combat-stat">🎯 {part.dexterity ?? 10}</span>
              <span className="combat-stat">🧠 {part.intelligence ?? 10}</span>
              <span className="combat-stat">❤️ {part.constitution ?? 10}</span>
            </>
          )}
        </div>
        {(part.effects || []).length > 0 && (
          <div className="combat-effect-banner" onClick={(event) => event.stopPropagation()}>
            <p className="combat-effect-banner-title">Aktive Effekte</p>
            {(part.effects || []).map((effect) => (
              <div key={effect.id} className={`combat-effect-row ${isBurnEffect(effect) ? 'is-burn' : ''}`}>
                <span title={effect.effect || effect.name}>
                  {isBurnEffect(effect) ? '🔥 ' : ''}
                  {effect.name}
                </span>
                <span className="combat-effect-row-actions">
                  {isBurnEffect(effect) && (
                    <button
                      type="button"
                      className="medieval-button small-button combat-burn-btn"
                      onClick={(event) => {
                        event.stopPropagation();
                        onTriggerEffect(effect.id);
                      }}
                    >
                      Schaden auslösen
                      {burnTriggerAmount(effect) > 0 ? ` ${burnTriggerAmount(effect)}` : ''}
                    </button>
                  )}
                  <button
                    type="button"
                    className="remove-item-btn"
                    title="Effekt beenden"
                    aria-label={`${effect.name} beenden`}
                    onClick={() => onRevokeEffect(effect.id)}
                  >
                    ✕
                  </button>
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </article>
  );
}
